// Качество данных: один экран на всё, чему в зеркале нельзя верить. ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ЭКРАН, ЕСЛИ КАЖДАЯ НАХОДКА УЖЕ ГДЕ-ТО ПОКАЗАНА. Она показана — и в этом беда.
// Расхождение состава живёт в «Расхождениях состава», висящие задания — в «Заданиях
// процессов», неразнесённый банк — в «Сквозных отчётах», себестоимость — в «Неликвидах».
// Человек, которому надо решить «за что браться первым», обходил семь разделов и
// складывал семь чисел в голове. Он этого не делает.
//
// ЧТО ЭКРАН ОБЯЗАН СКАЗАТЬ ПЕРВЫМ. Не «в базе N ошибок». Первым он говорит, сколько из
// найденного НАДО ЧИНИТЬ, а сколько — способ вести учёт. Экран, который показывает
// «8 693 расхождения» (на 28.07.2026), из которых 7 058 норма, учит не верить экрану;
// этот урок уже выучен на составе сделок, и здесь та же шкала и те же слова, чтобы
// не переучиваться.
//
// «ГДЕ ЧИНИТСЯ» — КОЛОНКА, БЕЗ КОТОРОЙ ЭКРАНА НЕ БЫЛО БЫ. Без адресата это список
// претензий: цифра есть, делать некому. Почти ничего из найденного не чинится у нас —
// зеркало не пишет ни в Bitrix, ни в 1С, ни на порталы и не должно.
//
// ДИНАМИКА ВАЖНЕЕ СРЕЗА. «30 счетов без ответственного» (на 28.07.2026) не значит ничего:
// это может быть наследие 2023 года, которое больше не растёт, а может быть вчерашняя
// поломка.
// Поэтому колонка «куда движется» стоит рядом с числом, а не прячется в подсказке. Где
// динамику посчитать нечем — так и написано словами: «ровно» было бы утверждением.
//
// НИ ОДНОГО СЛОВАРЯ «КОД → ПОДПИСЬ» ЗДЕСЬ НЕТ. Названия проверок, объяснения, места
// починки и степени приезжают из ядра вместе с числами. Свой словарь разъехался бы
// с ядром в день, когда там появится новая проверка.
//
// ЗАПИСИ ЗДЕСЬ НЕТ И БЫТЬ НЕ МОЖЕТ.
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { НетДоступа, useКачествоДанных, useСтрокиПроверки } from '../api/dq'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney } from '../lib/format'
import {
  ЗАПАСНАЯ_ПОДПИСЬ, классСтепени, когдаПосчитано, отобрать, поВажности, сколько,
  трендЗначком, трендСловами,
} from '../lib/dq'
import type { КучаПоМесту, КучаПоСтепени, ОтветПроверок, Проверка } from '../lib/dq'
import type { EntityKind } from '../lib/entities'

export default function DataQuality() {
  const q = useКачествоДанных()
  if (q.error instanceof НетДоступа) {
    return (
      <>
        <div className="page-head"><h1>Качество данных</h1></div>
        <Отказ />
      </>
    )
  }
  return (
    <>
      <Шапка данные={q.data} />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data ? <Таблица данные={q.data} обновляется={q.isFetching} /> : null}
      </QueryState>
    </>
  )
}

/**
 * Отказ по правам — объяснением, а не пустой таблицей.
 *
 * Пустая таблица здесь читалась бы как «нарушений нет», то есть как «в базе всё чисто».
 * Это худшая из возможных неправд на таком экране.
 */
function Отказ() {
  return (
    <div className="card">
      <div className="empty-title">Этот свод вам не открыт</div>
      <div className="empty-hint">
        Качество данных считается по всей базе сразу — сузить его до вашей зоны нечем:
        это был бы ответ на другой вопрос, и экран не смог бы сказать, на какой именно.
      </div>
    </div>
  )
}

function Шапка({ данные }: { данные?: ОтветПроверок }) {
  const самое_старое = (данные?.synced ?? [])
    .map((s) => s.age_min ?? 0)
    .reduce((a, b) => Math.max(a, b), 0)
  return (
    <div className="page-head">
      <h1>Качество данных</h1>
      <span className="spacer" />
      {данные && (
        <span className="hint">
          {когдаПосчитано(данные.cached_sec)}
          {самое_старое > 0 ? ` · зеркало отстаёт до ${Math.round(самое_старое)} мин` : ''}
        </span>
      )}
    </div>
  )
}

function Таблица({ данные, обновляется }: { данные: ОтветПроверок; обновляется: boolean }) {
  const [sp, setSp] = useSearchParams()
  // Отбор живёт в адресе: ссылку «покажи, что чинится в 1С» посылают в чат бухгалтерии,
  // и она обязана открыться тем же списком.
  const степень = sp.get('severity') ?? 'error'
  const место = sp.get('fix') ?? ''
  const область = sp.get('group') ?? ''
  const поиск = sp.get('q') ?? ''
  const скрыватьПустые = sp.get('empty') !== '1'
  const раскрыт = sp.get('open') ?? ''

  const правка = (что: Record<string, string | null>) => {
    const n = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(что)) {
      if (v) n.set(k, v)
      else n.delete(k)
    }
    setSp(n, { replace: true })
  }

  const видно = отобрать(данные.checks, {
    severity: степень === 'all' ? '' : степень,
    fix: место,
    group: область,
    q: поиск,
    hideEmpty: скрыватьПустые,
  }).sort(поВажности)

  const непосчитано = данные.checks.filter((п) => п.failed).length

  return (
    <>
      <Степени свод={данные.totals.by_severity} подписи={данные.severities}
               выбор={степень} выбрать={(с) => правка({ severity: с, open: null })} />

      <div className="notice" style={{ marginBottom: 12 }}>
        <b>Не всё найденное — ошибка.</b> Часть строк ниже это не порча данных, а способ
        вести учёт: акт на портале 73 со сделкой не связывают никогда, состав сделки в
        половине воронок не заводят вовсе, пробные записи заводили мы сами. Такие строки
        помечены «{ЗАПАСНАЯ_ПОДПИСЬ.ok}» и по умолчанию скрыты — но они здесь, чтобы их
        не нашли отдельно и не решили, что экран о них умолчал. Чинить надо то, что
        помечено «{ЗАПАСНАЯ_ПОДПИСЬ.error}».
      </div>

      {непосчитано > 0 && (
        <div className="notice err" style={{ marginBottom: 12 }}>
          <b>Не сработало проверок: {непосчитано}.</b> У них в таблице стоит «не
          посчитано», а не ноль: ноль означал бы «нарушений нет», и это была бы неправда.
        </div>
      )}

      <ПоМестам свод={данные.totals.by_fix} места={данные.places}
                выбор={место} выбрать={(м) => правка({ fix: м, open: null })} />

      <div className="card">
        <FilterBar id="dq" style={{ marginBottom: 12 }}>
          <div className="f">
            <label htmlFor="dq-group">Область</label>
            <select id="dq-group" value={область}
                    onChange={(e) => правка({ group: e.target.value || null, open: null })}>
              <option value="">Все области</option>
              {данные.groups.map((г) => (
                <option key={г.code} value={г.code}>{г.title}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="dq-fix">Где чинится</label>
            <select id="dq-fix" value={место}
                    onChange={(e) => правка({ fix: e.target.value || null, open: null })}>
              <option value="">Везде</option>
              {данные.places_order.map((м) => (
                <option key={м} value={м}>{данные.places[м]?.label ?? м}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="dq-q">Поиск по находкам</label>
            <input id="dq-q" value={поиск} placeholder="судно, ИНН, состав…"
                   onChange={(e) => правка({ q: e.target.value || null, open: null })} />
          </div>
          <div className="f">
            <label htmlFor="dq-empty">
              <input id="dq-empty" type="checkbox" checked={!скрыватьПустые}
                     onChange={(e) => правка({ empty: e.target.checked ? '1' : null })} />
              {' '}показывать проверки, где ничего не нашлось
            </label>
          </div>
          <span className="spacer" />
          <div className="hint" style={{ alignSelf: 'flex-end' }}>
            Показано {видно.length} из {данные.checks.length}
            {обновляется ? ' · обновляется…' : ''}
          </div>
        </FilterBar>

        {видно.length === 0
          ? (
            <div>
              <div className="empty-title">По этому отбору проверок нет</div>
              <div className="empty-hint">
                Это ответ «ничего не нашлось по выбранным фильтрам», а не «в базе всё
                чисто»: счётчики выше посчитаны по всем проверкам сразу.
              </div>
            </div>
          )
          : (
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Что не так</th>
                    <th className="num nowrap">Записей</th>
                    <th className="num nowrap">На сумму</th>
                    <th className="nowrap">Куда движется</th>
                    <th className="nowrap">Где чинится</th>
                    <th className="nowrap">Что это</th>
                  </tr>
                </thead>
                <tbody>
                  {видно.map((п) => (
                    <Строка key={п.code} п={п} окно={данные.window_days}
                            раскрыт={раскрыт === п.code}
                            раскрыть={() => правка({ open: раскрыт === п.code ? null : п.code })} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <div className="hint" style={{ marginTop: 10 }}>
          Денежного «итого» на экране нет намеренно: одна запись попадает в несколько
          проверок, а «сумма» у разных строк значит разное — величину расхождения, сумму
          сделки, остаток долга, модуль сальдо. Их сложение дало бы двадцать пять
          миллиардов и не значило бы ничего.
        </div>
      </div>

      <Оговорки заметки={данные.data_notes} />
    </>
  )
}

/** Счётчики по степеням: чинить, посмотреть, так ведут учёт. */
function Степени({
  свод, подписи, выбор, выбрать,
}: {
  свод: КучаПоСтепени[]
  подписи: Record<string, { label: string; what: string }>
  выбор: string
  выбрать: (с: string) => void
}) {
  const всего = свод.reduce((a, к) => a + к.n, 0)
  return (
    <div className="card">
      <div className="tiles">
        {свод.map((к) => (
          <div key={к.severity}
               className={`tile${к.severity === 'error' && к.n > 0 ? ' hot' : ''}`}
               title={подписи[к.severity]?.what ?? ''}>
            <div className="t-label">
              {подписи[к.severity]?.label ?? ЗАПАСНАЯ_ПОДПИСЬ[к.severity]}
            </div>
            <div className="t-sum">{к.n.toLocaleString('ru-RU')}</div>
            <div className="t-label">
              {к.checks} {к.checks === 1 ? 'проверка' : 'проверок'}
              {к.failed > 0 ? `, ${к.failed} не сработало` : ''}
            </div>
          </div>
        ))}
        <div className="tile" title="Все записи, которые нашли проверки, из чего бы они ни сложились">
          <div className="t-label">Найдено всего</div>
          <div className="t-sum">{всего.toLocaleString('ru-RU')}</div>
          <div className="t-label">записей</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
        {свод.map((к) => (
          <button key={к.severity} onClick={() => выбрать(к.severity)}
                  aria-pressed={выбор === к.severity}
                  title={подписи[к.severity]?.what ?? ''}
                  style={{ fontWeight: выбор === к.severity ? 600 : 400 }}>
            {подписи[к.severity]?.label ?? ЗАПАСНАЯ_ПОДПИСЬ[к.severity]} · {к.checks}
          </button>
        ))}
        <button onClick={() => выбрать('all')} aria-pressed={выбор === 'all'}
                style={{ fontWeight: выбор === 'all' ? 600 : 400 }}>
          Все проверки
        </button>
      </div>

      <div className="hint" style={{ marginTop: 10 }}>
        Счётчик — это записи, а не рубли. Одна запись попадает в несколько проверок сразу
        (у сделки нет контрагента, и она же закрыта раньше, чем заведена), поэтому «найдено
        всего» — верхняя граница, а не число различных испорченных записей.
      </div>
    </div>
  )
}

/** Кому это чинить. Отдельный блок, а не только колонка: по нему раздают работу. */
function ПоМестам({
  свод, места, выбор, выбрать,
}: {
  свод: КучаПоМесту[]
  места: Record<string, { label: string; who: string; why: string }>
  выбор: string
  выбрать: (м: string) => void
}) {
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Где это чинится</h2>
      <div className="hint" style={{ marginBottom: 10 }}>
        Зеркало не чинит почти ничего из этого и чинить не должно: оно обязано показать
        то, что есть. Правится всё в источнике — а в Bitrix на полях висят бизнес-процессы,
        которые ставят задачи живым людям, поэтому запись из CRM туда закрыта.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {свод.map((к) => (
          <button key={к.fix} onClick={() => выбрать(выбор === к.fix ? '' : к.fix)}
                  aria-pressed={выбор === к.fix}
                  title={`${места[к.fix]?.who ?? ''} — ${места[к.fix]?.why ?? ''}`}
                  style={{ fontWeight: выбор === к.fix ? 600 : 400 }}>
            {к.fix_label} · {к.checks} · {к.n.toLocaleString('ru-RU')}
          </button>
        ))}
      </div>
    </div>
  )
}

function Строка({
  п, окно, раскрыт, раскрыть,
}: { п: Проверка; окно: number; раскрыт: boolean; раскрыть: () => void }) {
  return (
    <>
      <tr style={раскрыт ? { fontWeight: 600 } : undefined}>
        <td data-label="Что не так">
          <button onClick={раскрыть} aria-expanded={раскрыт}
                  style={{ textAlign: 'left', fontWeight: 'inherit' }}>
            {п.title}
          </button>
          {п.failed && <> <span className="badge lose">не посчитано</span></>}
        </td>
        {/* Число и знаменатель вместе: голая находка и та же находка «из скольких»
            требуют разных решений, а выглядят одинаково. */}
        <td className="num" data-label="Записей" title={п.scope_note}>
          {п.failed ? <span className="muted">—</span> : сколько(п)}
        </td>
        <td className="num" data-label="На сумму">
          {п.has_money && п.money !== null
            ? fmtMoney(п.money, п.currency ?? 'RUB')
            : <span className="muted">—</span>}
        </td>
        <td className="nowrap" data-label="Куда движется" title={трендСловами(п, окно)}>
          {трендЗначком(п)}
        </td>
        <td className="nowrap" data-label="Где чинится">{п.fix_label}</td>
        <td className="nowrap" data-label="Что это">
          <span className={`badge ${классСтепени(п.severity)}`}>
            {ЗАПАСНАЯ_ПОДПИСЬ[п.severity]}
          </span>
        </td>
      </tr>
      {раскрыт && (
        <tr>
          <td colSpan={6}>
            <Подробности п={п} окно={окно} />
          </td>
        </tr>
      )}
    </>
  )
}

function Подробности({ п, окно }: { п: Проверка; окно: number }) {
  return (
    <div>
      <p style={{ marginTop: 0 }}>{п.what}</p>
      <div className="hint">
        <div>Куда движется: {трендСловами(п, окно)}
          {п.dated && п.n_new !== null && п.n_old !== null
            ? ` (${п.n_new} за последние ${окно} дн. против ${п.n_old} за предыдущие ${окно})`
            : ''}
          {п.dated && п.date_note ? ` — ${п.date_note}` : ''}
        </div>
        {п.n_nodate ? <div>Записей без даты: {п.n_nodate} — в динамику они не попали.</div> : null}
        {п.last_seen ? <div>Самая свежая: {fmtDate(п.last_seen)}.</div> : null}
        <div>Где чинится: {п.fix_label}.</div>
        {п.note ? <div style={{ marginTop: 6 }}>{п.note}</div> : null}
        {п.failed
          ? <div className="error" style={{ marginTop: 6 }}>Проверка не выполнилась: {п.failed}</div>
          : null}
      </div>
      {п.goto && (
        <div style={{ marginTop: 8 }}>
          <a href={п.goto}>Разобрать в профильном разделе →</a>
        </div>
      )}
      {!п.failed && (п.n ?? 0) > 0 && <Примеры п={п} />}
    </div>
  )
}

/**
 * Примеры записей. Догружаются только когда строку раскрыли: полсотни проверок × страница
 * строк — это полсотни запросов при открытии экрана.
 */
function Примеры({ п }: { п: Проверка }) {
  const [старт, setСтарт] = useState(0)
  const q = useСтрокиПроверки(п.code, старт)
  const строки = q.data?.rows ?? []
  const колонки = строки.length ? Object.keys(строки[0]) : []
  return (
    <div style={{ marginTop: 10 }}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <>
          {строки.length === 0
            ? <div className="hint">Примеров нет.</div>
            : (
              <div className="table-wrap cards">
                <table>
                  <thead>
                    <tr>{колонки.map((к) => <th key={к}>{к}</th>)}</tr>
                  </thead>
                  <tbody>
                    {строки.map((r, i) => (
                      <tr key={String(r.id ?? i)}>
                        {/* Денежная колонка примера — такая же числовая колонка, как в
                            остальных таблицах CRM: без `.num` разряды в ней не встают
                            друг под друга и «1 200 000» против «990 000» приходится
                            читать, а не видеть. Список имён один на разметку и на разбор
                            значения — второй разъехался бы с первым. */}
                        {колонки.map((к) => (
                          <td key={к} data-label={к} className={ДЕНЕЖНЫЕ.has(к) ? 'num' : undefined}>
                            {Ячейка(к, r[к], п)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          {q.data && q.data.total > q.data.limit && (
            <div className="pager">
              <button disabled={старт === 0 || q.isFetching}
                      onClick={() => setСтарт(Math.max(0, старт - q.data!.limit))}>
                ← назад
              </button>
              <span className="muted">
                {старт + 1}–{Math.min(старт + q.data.limit, q.data.total)} из {q.data.total}
              </span>
              <button disabled={q.data.next == null || q.isFetching}
                      onClick={() => setСтарт(q.data!.next!)}>
                вперёд →
              </button>
            </div>
          )}
          <div className="hint" style={{ marginTop: 6 }}>
            Это иллюстрация к числу, а не выгрузка: страница ограничена. Названия колонок
            приходят как есть из ядра — придумывать им подписи здесь значило бы завести
            второй словарь, который разъедется с первым.
          </div>
        </>
      </QueryState>
    </div>
  )
}

/**
 * Имена колонок примера, в которых ядро отдаёт деньги. Один список на два дела: по нему
 * значение форматируется рублями и по нему же ячейка получает класс `.num`. Два списка
 * разъехались бы молча — сумма стала бы выравниваться влево или наоборот.
 */
const ДЕНЕЖНЫЕ = new Set(['sum', 'extra', 'lines_sum', 'delta'])

/**
 * Одна ячейка примера. Догадок минимум и все по имени колонки, которое задаёт ядро:
 * `id` — ссылка на карточку, если проверка сказала, на какую; `sum` — деньги;
 * `when`/`closed_at` — дата. Остальное показываем как есть, а не угадываем формат.
 */
function Ячейка(имя: string, v: unknown, п: Проверка) {
  if (v === null || v === undefined || v === '') return <span className="muted">—</span>
  if (имя === 'id' && п.link) {
    return <EntityLink kind={п.link as EntityKind} id={v as string | number} />
  }
  if (имя === 'deal_id') return <EntityLink kind="deal" id={v as string | number} />
  if (ДЕНЕЖНЫЕ.has(имя)) {
    return fmtMoney(v as number, п.currency ?? 'RUB')
  }
  if (имя === 'when' || имя === 'closed_at' || имя === 'valid_to') {
    return fmtDate(String(v))
  }
  return String(v)
}

/** Оговорки о данных — текстом ядра, а не нашим. */
function Оговорки({ заметки }: { заметки: Record<string, string> }) {
  const список = Object.entries(заметки)
  if (!список.length) return null
  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Чего этот экран не видит</h2>
      <ul className="plain-list">
        {список.map(([к, т]) => <li key={к} className="hint">{т}</li>)}
      </ul>
    </div>
  )
}
